class HashtagService {
  constructor() {
    this.serverUrl = 'http://localhost:3001';
    this.apiUrl = '/api/generate-hashtags';
    this.useRealAPI = false; // Флаг для использования реального API
    
    // Список популярных хэштегов по категориям
    this.popularHashtags = {
      news: ['новости', 'события', 'актуально', 'важно', 'сегодня'],
      tech: ['технологии', 'инновации', 'гаджеты', 'digital', 'tech'],
      politics: ['политика', 'власть', 'выборы', 'госдума', 'президент'],
      economy: ['экономика', 'финансы', 'бизнес', 'деньги', 'инвестиции'],
      sport: ['спорт', 'футбол', 'хоккей', 'чемпионат', 'олимпиада'],
      culture: ['культура', 'искусство', 'кино', 'музыка', 'театр'],
      science: ['наука', 'исследования', 'открытие', 'ученые', 'прогресс']
    };
    
    // Ключевые слова для определения категории
    this.categoryKeywords = {
      tech: ['технологии', 'гаджет', 'смартфон', 'компьютер', 'интернет', 'программ', 'приложени', 'цифров', 'робот', 'искусственный интеллект', 'блокчейн', 'криптовалют'],
      politics: ['политик', 'президент', 'правительств', 'парламент', 'выбор', 'закон', 'депутат', 'министр', 'госдума', 'конституци', 'оппозици'],
      economy: ['экономик', 'финанс', 'бизнес', 'деньги', 'банк', 'инвестиц', 'акци', 'рынок', 'кризис', 'инфляци', 'бюджет', 'налог'],
      sport: ['спорт', 'футбол', 'хоккей', 'баскетбол', 'теннис', 'чемпионат', 'олимпиад', 'матч', 'турнир', 'медал', 'игр'],
      culture: ['культур', 'искусств', 'кино', 'фильм', 'музык', 'театр', 'выставк', 'концерт', 'книг', 'литератур', 'премьер'],
      science: ['наук', 'исследовани', 'открыти', 'учен', 'эксперимент', 'космос', 'медицин', 'физик', 'биолог', 'технолог']
    };
    
    // Получаем URL сервера из NewsService, если он был определен
    try {
      const NewsService = require('./NewsService').default;
      if (NewsService.serverUrl) {
        this.serverUrl = NewsService.serverUrl;
      }
    } catch (e) {
      console.log('Не удалось получить URL сервера из NewsService');
    }
  }

  // Метод для генерации хэштегов на основе заголовка и контента
  async generateHashtags(title, content) {
    try {
      console.log(`Генерация хэштегов для новости: ${title}`);
      
      if (this.useRealAPI) {
        // Используем реальный API для генерации хэштегов
        return await this.generateHashtagsWithAPI(title, content);
      } else {
        // Используем локальную логику для генерации хэштегов
        return await this.generateHashtagsLocally(title, content);
      }
    } catch (error) {
      console.error('Ошибка при генерации хэштегов:', error);
      return ['новости', 'события'];
    }
  }
  
  // Метод для генерации хэштегов с использованием API
  async generateHashtagsWithAPI(title, content) {
    try {
      const url = `${this.serverUrl}${this.apiUrl}`;
      console.log(`Отправка запроса к API хэштегов: ${url}`);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title, content })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Ошибка API: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const data = await response.json();
      return data.hashtags;
    } catch (error) {
      console.error('Ошибка при запросе к API хэштегов:', error);
      // Если API недоступен, используем локальную логику
      return this.generateHashtagsLocally(title, content);
    }
  }
  
  // Метод для локальной генерации хэштегов
  async generateHashtagsLocally(title, content) {
    // Определяем категорию новости
    const category = this.determineCategory(title, content);
    
    // Получаем базовые хэштеги для категории
    const baseHashtags = this.popularHashtags[category] || this.popularHashtags.news;
    
    // Извлекаем ключевые слова из заголовка
    const titleWords = title.toLowerCase()
      .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '')
      .split(' ')
      .filter(word => word.length > 4)
      .slice(0, 2);
    
    // Формируем хэштеги из ключевых слов
    const keywordHashtags = titleWords.map(word => this.normalizeHashtag(word));
    
    // Объединяем базовые хэштеги и хэштеги из ключевых слов
    let hashtags = [...baseHashtags.slice(0, 3), ...keywordHashtags];
    
    // Удаляем дубликаты
    hashtags = [...new Set(hashtags)];
    
    // Имитация задержки запроса к API
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return hashtags;
  }
  
  // Метод для определения категории новости
  determineCategory(title, content) {
    const combinedText = (title + ' ' + content).toLowerCase();
    
    // Проверяем каждую категорию на наличие ключевых слов
    for (const [category, keywords] of Object.entries(this.categoryKeywords)) {
      for (const keyword of keywords) {
        if (combinedText.includes(keyword)) {
          return category;
        }
      }
    }
    
    // Если категория не определена, возвращаем 'news'
    return 'news';
  }
  
  // Метод для нормализации слова в хэштег
  normalizeHashtag(word) {
    // Транслитерация русских букв в латиницу (для международных хэштегов)
    const translitMap = {
      'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'e',
      'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
      'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
      'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch', 'ъ': '',
      'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya'
    };
    
    // Оставляем оригинальное слово для русскоязычных хэштегов
    return word.trim().replace(/\s+/g, '');
  }
}

export default new HashtagService();